import React from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FaCalendar, FaUser, FaTag, FaTwitter, FaLinkedin, FaFacebook, FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import './BlogDetail.css';

const BlogDetail = () => {
  const { slug } = useParams();

  // Blog Data with enhanced content
  const blogs = [
    {
      slug: "future-of-web-development-2026",
      title: "Future of Web Development in 2026 (Complete Guide)",
      description: "Learn the latest trends in web development including AI, frameworks and tools shaping the future.",
      author: "Abhishek Yadav",
      authorBio: "Senior Full Stack Developer with 1.5+ years of experience in web technologies.",
      authorAvatar: "https://ui-avatars.com/api/?name=Abhishek+Yadav&background=6366f1&color=fff",
      date: "March 5, 2024",
      readTime: "8 min read",
      category: "Web Development",
      image: "https://images.unsplash.com/photo-1498050108023-c5249f4df085",
      tags: ["Web Development", "AI", "Frameworks", "2026 Trends"],
      content: `
        <p>Web development is evolving rapidly in 2026. With the rise of AI tools, modern frameworks and cloud computing, developers must stay updated with the latest trends and technologies to remain competitive in the industry.</p>
        
        <h2>Key Trends Shaping Web Development</h2>
        <p>The landscape of web development is changing faster than ever before. Here are the most significant trends that are defining the future:</p>
        
        <h3>1. AI-Powered Development Tools</h3>
        <p>Artificial Intelligence is revolutionizing how we write code. Tools like GitHub Copilot, ChatGPT, and various AI assistants are becoming essential parts of every developer's toolkit. These tools help with code completion, bug detection, and even generating entire components.</p>
        
        <h3>2. Serverless Architecture</h3>
        <p>Serverless computing continues to gain traction, allowing developers to focus on code rather than infrastructure management. Platforms like AWS Lambda, Vercel, and Netlify make deployment seamless and cost-effective.</p>
        
        <h3>3. Progressive Web Apps (PWAs)</h3>
        <p>PWAs are bridging the gap between web and mobile applications, offering native-like experiences with the accessibility of the web. They're becoming the standard for businesses looking to reach users across all devices.</p>
        
        <h3>4. Modern JavaScript Frameworks</h3>
        <p>React, Next.js, Vue, and Svelte are leading the industry with improved performance, better developer experience, and enhanced features. The competition between frameworks drives innovation and gives developers more powerful tools.</p>
        
        <h2>The Role of WebAssembly</h2>
        <p>WebAssembly (Wasm) is enabling high-performance applications in the browser, allowing languages like Rust, C++, and Go to run efficiently on the web. This opens up new possibilities for computationally intensive applications.</p>
        
        <h2>Conclusion</h2>
        <p>Web development in 2026 is faster, smarter and more efficient than ever before. Staying updated with these trends is crucial for developers who want to build modern, scalable applications. The future of web development is bright, with AI and automation making development more accessible while enabling more complex applications.</p>
      `,
    },
    {
      slug: "build-mobile-app-react-native",
      title: "How to Build Mobile Apps with React Native (Step-by-Step)",
      description: "Complete guide to building mobile apps using React Native for Android and iOS.",
      author: "ZustandTech Team",
      authorBio: "Expert mobile development team specializing in cross-platform solutions.",
      authorAvatar: "https://ui-avatars.com/api/?name=ZustandTech+Team&background=6366f1&color=fff",
      date: "March 3, 2024",
      readTime: "12 min read",
      category: "Mobile Apps",
      image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c",
      tags: ["React Native", "Mobile Development", "iOS", "Android"],
      content: `
        <p>React Native has become one of the most popular frameworks for building cross-platform mobile applications. With a single codebase, you can target both iOS and Android platforms while maintaining native performance and user experience.</p>
        
        <h2>Getting Started with React Native</h2>
        <p>Before diving into development, ensure you have the necessary tools installed. Here's a step-by-step guide to build your first React Native app:</p>
        
        <h3>Step 1: Install React Native CLI</h3>
        <p>Start by installing the React Native CLI globally:</p>
        <pre><code>npm install -g react-native-cli</code></pre>
        
        <h3>Step 2: Create a New Project</h3>
        <p>Initialize your React Native project:</p>
        <pre><code>npx react-native init MyAwesomeApp</code></pre>
        
        <h3>Step 3: Setup Development Environment</h3>
        <p>Configure your environment for iOS (Xcode) and Android (Android Studio). Make sure all dependencies are properly installed.</p>
        
        <h3>Step 4: Build UI Components</h3>
        <p>React Native uses components similar to React. Start building your user interface with native components like View, Text, ScrollView, and TouchableOpacity.</p>
        
        <h3>Step 5: Connect to Backend</h3>
        <p>Use Fetch API or Axios to connect your app to REST APIs or GraphQL endpoints. Implement state management with Redux or Context API.</p>
        
        <h3>Step 6: Test Your Application</h3>
        <p>Test thoroughly on both platforms using the built-in testing tools and emulators.</p>
        
        <h3>Step 7: Deploy to App Stores</h3>
        <p>Prepare your app for production and submit to the Apple App Store and Google Play Store.</p>
        
        <h2>Best Practices</h2>
        <ul>
          <li>Use TypeScript for better type safety</li>
          <li>Implement proper navigation with React Navigation</li>
          <li>Optimize performance with memoization and proper component structure</li>
          <li>Use platform-specific code when necessary</li>
          <li>Implement error boundaries for better error handling</li>
        </ul>
        
        <h2>Conclusion</h2>
        <p>React Native allows you to build fast and scalable apps for both Android and iOS with a single codebase. The framework continues to evolve with better performance, new features, and improved developer experience.</p>
      `,
    },
    {
      slug: "ai-in-business-2026",
      title: "AI in Business: How Artificial Intelligence is Changing Everything",
      description: "Discover how AI is transforming industries and helping businesses grow faster.",
      author: "Dr. Sarah Johnson",
      authorBio: "AI Researcher and Business Consultant specializing in enterprise AI adoption.",
      authorAvatar: "https://ui-avatars.com/api/?name=Sarah+Johnson&background=6366f1&color=fff",
      date: "March 1, 2024",
      readTime: "10 min read",
      category: "AI/ML",
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d",
      tags: ["Artificial Intelligence", "Machine Learning", "Business Transformation"],
      content: `
        <p>Artificial Intelligence is revolutionizing businesses across the world. From customer service to supply chain management, AI is becoming an integral part of modern business operations.</p>
        
        <h2>Benefits of AI in Business</h2>
        
        <h3>1. Automation of Repetitive Tasks</h3>
        <p>AI-powered automation can handle routine tasks, freeing up human employees for more strategic work. This leads to increased efficiency and reduced operational costs.</p>
        
        <h3>2. Better Decision Making</h3>
        <p>Machine learning algorithms can analyze vast amounts of data to provide actionable insights, helping business leaders make informed decisions quickly.</p>
        
        <h3>3. Improved Customer Experience</h3>
        <p>AI chatbots and personalized recommendations enhance customer interactions, leading to higher satisfaction and loyalty.</p>
        
        <h3>4. Predictive Analytics</h3>
        <p>AI models can forecast trends, customer behavior, and market changes, allowing businesses to stay ahead of the competition.</p>
        
        <h2>AI Tools Transforming Business</h2>
        <ul>
          <li><strong>ChatGPT and LLMs:</strong> Revolutionizing content creation, customer support, and internal communications</li>
          <li><strong>Machine Learning Platforms:</strong> TensorFlow, PyTorch for custom model development</li>
          <li><strong>Computer Vision:</strong> For quality control, security, and visual recognition</li>
          <li><strong>NLP Tools:</strong> For sentiment analysis, document processing, and language understanding</li>
        </ul>
        
        <h2>Challenges and Considerations</h2>
        <p>While AI offers tremendous benefits, businesses must consider data privacy, ethical implications, and the need for skilled personnel. Successful AI implementation requires a strategic approach and proper governance.</p>
        
        <h2>Conclusion</h2>
        <p>AI is no longer just a buzzword—it's a fundamental technology that's reshaping how businesses operate. Organizations that embrace AI will gain significant competitive advantages in the coming years.</p>
      `,
    }
  ];

  // Find current blog
  const blog = blogs.find((b) => b.slug === slug);
  
  // Find related posts (exclude current)
  const relatedPosts = blogs.filter((b) => b.slug !== slug).slice(0, 2);
  
  // Find next and previous posts
  const currentIndex = blogs.findIndex((b) => b.slug === slug);
  const prevPost = currentIndex > 0 ? blogs[currentIndex - 1] : null;
  const nextPost = currentIndex < blogs.length - 1 ? blogs[currentIndex + 1] : null;

  if (!blog) {
    return (
      <div className="blog-detail">
        <div className="not-found">
          <h1>Blog Post Not Found</h1>
          <p>Sorry, the article you're looking for doesn't exist or has been moved.</p>
          <Link to="/blog" className="back-btn">
            <FaArrowLeft /> Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  // Share functionality
  const shareOnTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(blog.title)}&url=${encodeURIComponent(window.location.href)}`, '_blank');
  };
  
  const shareOnLinkedIn = () => {
    window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(blog.title)}`, '_blank');
  };
  
  const shareOnFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank');
  };

  return (
    <div className="blog-detail">
      <Helmet>
        <title>{blog.title} | ZustandTech Blog</title>
        <meta name="description" content={blog.description} />
        <meta name="keywords" content={blog.tags.join(', ')} />
        <link rel="canonical" href={`https://www.zustandtech.com/blog/${blog.slug}`} />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={blog.description} />
        <meta property="og:image" content={blog.image} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">
  {`
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "${blog.title}",
      "description": "${blog.description}",
      "image": "${blog.image}",
      "author": {
        "@type": "Person",
        "name": "${blog.author}"
      },
      "publisher": {
        "@type": "Organization",
        "name": "ZustandTech",
        "logo": {
          "@type": "ImageObject",
          "url": "https://www.zustandtech.com/ar.png"
        }
      },
      "datePublished": "${blog.date}",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://www.zustandtech.com/blog/${blog.slug}"
      }
    }
  `}
</script>
      </Helmet>

      <div className="blog-detail-container">
        {/* Blog Header */}
        <header className="blog-header">
          <h1>{blog.title}</h1>
          <div className="blog-meta">
            <span><FaUser /> {blog.author}</span>
            <span><FaCalendar /> {blog.date}</span>
            <span>📖 {blog.readTime}</span>
          </div>
        </header>

        {/* Featured Image */}
        <div className="blog-featured-image">
          <img src={blog.image} alt={blog.title} loading="lazy" />
        </div>

        {/* Blog Content */}
        <div 
          className="blog-content"
          dangerouslySetInnerHTML={{ __html: blog.content }}
        />
<section className="blog-seo-section">

<h2>
  Software Development Insights by ZustandTech
</h2>

<p>
  ZustandTech publishes expert insights about
  React.js development, AI solutions,
  mobile app development, cloud computing,
  SaaS platforms, and enterprise software technologies.
</p>

<h2>
  Modern Web & AI Development Trends
</h2>

<p>
  Our technical blogs help businesses,
  startups, and developers understand
  modern software architecture,
  artificial intelligence,
  scalable applications,
  cloud systems,
  and future technology trends.
</p>

</section>
        {/* Tags Section */}
        {blog.tags && blog.tags.length > 0 && (
          <div className="blog-tags-section">
            <div className="blog-tags-list">
              {blog.tags.map((tag, index) => (
                <span key={index} className="blog-tag">
                  <FaTag /> {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Author Section */}
        <div className="author-section">
          <img 
            src={blog.authorAvatar} 
            alt={blog.author} 
            className="author-avatar-large" 
          />
          <div className="author-info">
            <h3>{blog.author}</h3>
            <p>{blog.authorBio || "Tech enthusiast and expert in modern web development."}</p>
          </div>
        </div>

        {/* Share Section */}
        <div className="share-section">
          <div className="share-buttons">
            <button onClick={shareOnTwitter} className="share-btn twitter">
              <FaTwitter /> Twitter
            </button>
            <button onClick={shareOnLinkedIn} className="share-btn linkedin">
              <FaLinkedin /> LinkedIn
            </button>
            <button onClick={shareOnFacebook} className="share-btn facebook">
              <FaFacebook /> Facebook
            </button>
          </div>
        </div>

        {/* Navigation */}
        <div className="blog-navigation">
          {prevPost && (
            <Link to={`/blog/${prevPost.slug}`} className="nav-btn prev">
              <FaArrowLeft /> Previous
            </Link>
          )}
          {nextPost && (
            <Link to={`/blog/${nextPost.slug}`} className="nav-btn next">
              Next <FaArrowRight />
            </Link>
          )}
        </div>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="related-posts">
            <h3>You Might Also Like</h3>
            <div className="related-grid">
              {relatedPosts.map((post) => (
                <Link to={`/blog/${post.slug}`} key={post.slug} className="related-card">
                  <div className="related-image">
                    <img src={post.image} alt={post.title} loading="lazy" />
                  </div>
                  <div className="related-content">
                    <h4>{post.title}</h4>
                    <p className="related-date">{post.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Back to Blog */}
        <div className="back-to-blog">
          <Link to="/blog" className="back-btn">
            <FaArrowLeft /> Back to All Articles
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;